import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { AccountState } from 'src/app/store/account/account.state';
import { MyStudentsState } from 'src/app/store/my-students/my-students.state';
import { ToastController } from 'src/app/core/controllers/toast';

/**
 * Goal Drafter — disability guard.
 *
 * Every student must have at least one disability identified before a goal can
 * be drafted for them. This guard blocks the Goal Drafter for a `:studentId`
 * whose `disabilityType` is empty and redirects to that student's notebook
 * profile with `?identifyDisability=1` (which auto-opens the add-disability
 * picker), plus a toast explaining why.
 *
 * Fail-open by design: if the caseload students store hasn't hydrated within
 * the timeout, or the id doesn't resolve to a known student, the guard admits
 * navigation rather than wrongly blocking. The param-less compat entry (no
 * `:studentId`) is never gated.
 *
 * @example
 * // In app.routes.ts, appended after licenseGuardFor('tools')
 * canActivate: [authGuard, ..., licenseGuardFor('tools'), goalDisabilityGuard]
 */
export const goalDisabilityGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const accountState = inject(AccountState);
  const myStudentsState = inject(MyStudentsState);
  const toastController = inject(ToastController);

  const studentId = route.paramMap.get('studentId');
  if (!studentId) return true;

  // Wait for the caseload students store to be ready so we read a real record
  // rather than fail-opening on a not-yet-loaded one. Fail-open on timeout.
  try {
    await firstValueFrom(
      toObservable(myStudentsState.isReady).pipe(
        filter(ready => ready === true),
        timeout(10000),
      ),
    );
  } catch {
    return true;
  }

  const student = myStudentsState.getStudentById(studentId);
  if (!student) return true;
  if (student.disabilityType?.length) return true;

  await toastController.present({
    message: 'Identify a disability for this student before creating a goal.',
    color: 'warning',
    position: 'top',
    duration: 3000,
  });

  const tree = router.parseUrl(accountState.notebookUrl('caseload', studentId, 'profile'));
  tree.queryParams = { identifyDisability: '1' };
  return tree;
};
